import type { QueryTarget } from "@/shared/domain/query";

export type RobotoConfigKey =
  | "ai_dataset_summary_prompt"
  | "ai_common_knowledge"
  | "org_default_views";

export type RobotoUserConfigKey =
  | "user_default_layout_id"
  | "collection_search_table_preferences"
  | "dataset_search_table_preferences"
  | "device_search_table_preferences"
  | "file_search_table_preferences"
  | "topic_search_table_preferences"
  | "message_path_search_table_preferences"
  | "event_search_table_preferences"
  | "session_search_table_preferences"
  | "client_tool_preferences"
  | "date_time_format_preference"
  | "workspace_settings_defaults";

/**
 * How one user has set up one table: the layout they arranged, and the View
 * they start from.
 *
 * Mirrors `roboto_internal.config.record.TableUIPreferences`. Both kinds of
 * preference live in one record deliberately — `columns` and `rows_per_page`
 * are the layout built by hand, `default_view_id` names a saved View to apply
 * on arrival, and a View supersedes the hand-built layout while it is applied.
 *
 * Merged field by field on the server, so writing one leaves the others alone.
 * `null` clears a field; omitting the key leaves it unchanged.
 */
export interface TableUIPreferences {
  columns?: string[];
  rows_per_page?: number;
  /**
   * View to apply when this user opens this table.
   *
   * `null` to clear it — omitting the key means "leave unchanged", so an
   * unset cannot be expressed by absence. Not validated against the View it
   * names: the View may since have been deleted or become invisible, so a
   * reader that cannot resolve it falls back to showing everything.
   */
  default_view_id?: string | null;
}

export interface TableSearchPreferencesConfig {
  version: number;
  // Key is org ID
  org_scoped_preferences: Record<string, TableUIPreferences>;
}

/** Org config key under which {@link OrgDefaultViewsConfig} is stored. */
export const ORG_DEFAULT_VIEWS_KEY = "org_default_views";

/**
 * The View each table in an org opens on for members who have chosen no
 * default of their own. Only org admins can write it.
 *
 * Mirrors `roboto_internal.config.core_model.OrgDefaultViewsConfig`. One
 * record per org covers every table; a table absent from
 * `default_view_id_by_target` has no org default. Unlike the per-user table
 * preferences, the org config endpoint replaces the whole value, so a writer
 * changing one table's entry must send every other entry back unchanged or
 * they are lost.
 */
export interface OrgDefaultViewsConfig {
  version: number;
  default_view_id_by_target: Partial<Record<QueryTarget, string>>;
}

/**
 * Stand-in for the `null` the config service returns when an org has never
 * set a default, so readers and writers handle one shape.
 */
export const EMPTY_ORG_DEFAULT_VIEWS: OrgDefaultViewsConfig = {
  version: 1,
  default_view_id_by_target: {},
};
